import React, { useEffect, useState, useMemo } from "react";
import FinancingSection from "./sections/FinancingSection";
import AcquisitionCosts from "./sections/AcquisitionCosts";
import BasicInfo from "./sections/BasicInfo";
import RevenueSection from "./sections/RevenueSection";
import OperatingExpensesSection from "./sections/OperatingExpensesSection";
import { parseLocaleNumber } from "./FormattedNumberInput";
import calculateWelcomeTax from "../utils/calculateWelcomeTax";
import calculateRentability from "../utils/calculateRentability";
import { saveScenario, updateScenario } from "../services/dataService";
import { useLanguage } from "../contexts/LanguageContext";
import { useRegisterAppBack } from "../hooks/useAppBackButton";
import { isOwnedDeal, isInPlaceLoanFinancing, isZeroDebtFinancing } from "../utils/dealPurpose";

export default function FinancingScenarioForm({
  onBack,
  onSaved,
  propertyId,
  initialScenario = {},
  type = "initialFinancing",
  property,
  advancedExpenses,
  onSaveScenario = null,
  onUpdateScenario = null,
  creatorUid = null,
}) {
  const [scenario, setScenario] = useState({
    title: "",
    financing: {},
    acquisitionCosts: {},
    ...initialScenario,
  });

  const [lockedFields, setLockedFields] = useState({
    debtCoverage: true,
    welcomeTax: true,
  });

  const { t } = useLanguage();

  useEffect(() => {
    setScenario({
      title: "",
      financing: {},
      acquisitionCosts: {},
      ...initialScenario,
    });
  }, [initialScenario.id]);

  const handleFinancingChange = (financing, field) => {
    if (field === "debtCoverageRatio") {
      setLockedFields((prev) => ({ ...prev, debtCoverage: false }));
    } else if (field === "financingType") {
      setLockedFields((prev) => ({ ...prev, debtCoverage: true }));
    }
    setScenario((prev) => ({ ...prev, financing }));
  };

  const handleCostsChange = (costs) => {
    setScenario((prev) => ({ ...prev, acquisitionCosts: costs }));
  };

  const handleChange = (field, value) => {
    setScenario((prev) => ({ ...prev, [field]: value }));
  };

  const computeTotalCosts = () => {
    const keys = advancedExpenses
      ? [
          "inspection",
          "environmental1",
          "environmental2",
          "environmental3",
          "otherFees",
          "appraiser",
          "notary",
          "renovations",
          "cmhcAnalysis",
          "cmhcTax",
          "welcomeTax",
        ]
      : [
          "expertises",
          "notary",
          "renovations",
          "cmhcAnalysis",
          "cmhcTax",
          "otherFees",
          "welcomeTax",
        ];

    let total = keys.reduce(
      (sum, key) =>
        sum + Number(parseLocaleNumber(scenario.acquisitionCosts[key]) || 0),
      0
    );

    if (scenario.financing.financingType === "private") {
      const fee = Number(parseLocaleNumber(scenario.financing.originationFee) || 0);
      const loan = analysis?.maxLoanAmount || 0;
      const amount =
        (scenario.financing.originationFeeType || "percentage") === "currency"
          ? fee
          : (loan * fee) / 100;
      total += amount;
    }

    return total;
  };

  const analysis = useMemo(() => {
    if (!property) return null;
    const combinedProperty = { ...property, ...scenario.financing };
    return calculateRentability(combinedProperty, advancedExpenses);
  }, [property, scenario.financing, advancedExpenses]);

  useEffect(() => {
    if (!lockedFields.welcomeTax) return;
    if (isOwnedDeal(property)) {
      if (scenario.acquisitionCosts.welcomeTax) {
        setScenario((prev) => ({
          ...prev,
          acquisitionCosts: { ...prev.acquisitionCosts, welcomeTax: '' },
        }));
      }
      return;
    }
    const purchasePrice = parseFloat(property?.purchasePrice) || 0;
    if (purchasePrice > 0) {
      const welcomeTax = Math.round(calculateWelcomeTax(purchasePrice)).toString();
      if (scenario.acquisitionCosts.welcomeTax !== welcomeTax) {
        setScenario((prev) => ({
          ...prev,
          acquisitionCosts: { ...prev.acquisitionCosts, welcomeTax },
        }));
      }
    }
  }, [
    property?.purchasePrice,
    property?.dealPurpose,
    lockedFields.welcomeTax,
    scenario.acquisitionCosts.welcomeTax,
  ]);

  useEffect(() => {
    const financingType = scenario.financing.financingType;
    const units = parseInt(property?.numberOfUnits) || 0;
    if (["cmhc", "cmhc_aph"].includes(financingType) && units > 0) {
      const analysisAmount = (units * 150).toString();
      if (scenario.acquisitionCosts.cmhcAnalysis !== analysisAmount) {
        setScenario((prev) => ({
          ...prev,
          acquisitionCosts: { ...prev.acquisitionCosts, cmhcAnalysis: analysisAmount },
        }));
      }
    } else if (scenario.acquisitionCosts.cmhcAnalysis) {
      setScenario((prev) => ({
        ...prev,
        acquisitionCosts: { ...prev.acquisitionCosts, cmhcAnalysis: "" },
      }));
    }
  }, [
    scenario.financing.financingType,
    property?.numberOfUnits,
    scenario.acquisitionCosts.cmhcAnalysis,
  ]);

  useEffect(() => {
    const financingType = scenario.financing.financingType;
    if (["cmhc", "cmhc_aph"].includes(financingType)) {
      const taxAmount = analysis?.cmhcTax
        ? Math.round(analysis.cmhcTax).toString()
        : "";
      if (scenario.acquisitionCosts.cmhcTax !== taxAmount) {
        setScenario((prev) => ({
          ...prev,
          acquisitionCosts: { ...prev.acquisitionCosts, cmhcTax: taxAmount },
        }));
      }
    } else if (scenario.acquisitionCosts.cmhcTax) {
      setScenario((prev) => ({
        ...prev,
        acquisitionCosts: { ...prev.acquisitionCosts, cmhcTax: "" },
      }));
    }
  }, [analysis?.cmhcTax, scenario.financing.financingType, scenario.acquisitionCosts.cmhcTax]);

  useEffect(() => {
    if (!lockedFields.debtCoverage) return;
    const financingType = scenario.financing.financingType;
    const units = parseInt(property?.numberOfUnits) || 1;
    let newRatio = "";
    if (financingType === "cmhc") {
      newRatio = units >= 7 ? "1.3" : "1.1";
    } else if (financingType === "cmhc_aph") {
      newRatio = "1.1";
    } else if (
      financingType &&
      financingType !== "private" &&
      !isInPlaceLoanFinancing(financingType) &&
      !isZeroDebtFinancing(financingType)
    ) {
      newRatio = "1.15";
    }
    if (scenario.financing.debtCoverageRatio !== newRatio) {
      setScenario((prev) => ({
        ...prev,
        financing: { ...prev.financing, debtCoverageRatio: newRatio },
      }));
    }
  }, [
    scenario.financing.financingType,
    property?.numberOfUnits,
    lockedFields.debtCoverage,
  ]);

  const handleSave = async () => {
    const { id, ...dataWithoutId } = scenario;
    const data = { ...dataWithoutId, type };
    if (onSaveScenario && onUpdateScenario) {
      if (id) {
        await onUpdateScenario(id, data);
        onSaved && onSaved({ id, ...data });
      } else {
        const newId = await onSaveScenario(data);
        onSaved && onSaved({ id: newId, ...data });
      }
    } else if (id) {
      await updateScenario(propertyId, id, data);
      onSaved && onSaved({ id, ...data });
    } else {
      const newId = await saveScenario(propertyId, data, creatorUid ?? undefined);
      onSaved && onSaved({ id: newId, ...data });
    }
  };

  const titleText = t("scenarioForm.financing.title");
  useRegisterAppBack(onBack, Boolean(onBack));

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold min-w-0" data-onboarding="scenario-header">{titleText}</h2>
            {onBack && (
              <button
                onClick={onBack}
                className="text-gray-600 hover:text-gray-800 self-start sm:self-auto shrink-0"
              >
                ← {t('back')}
              </button>
            )}
          </div>

          <div className="space-y-8">
            <div className="border rounded-lg p-4 sm:p-6" data-onboarding="scenario-title">
              <h2 className="text-lg font-semibold mb-4 text-gray-700">
                {t('scenarioForm.titleLabel')}
              </h2>
              <input
                type="text"
                value={scenario.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full border rounded p-2"
                placeholder={t('scenarioForm.titlePlaceholder')}
              />
            </div>

            {property && (
              <div className="space-y-8" data-onboarding="scenario-property-data">
                <BasicInfo
                  property={property}
                  onChange={() => {}}
                  advancedExpenses={advancedExpenses}
                  readOnly
                  disablePlaceAutocomplete
                  collapsible
                  defaultCollapsed
                />
                <RevenueSection
                  revenue={property}
                  onChange={() => {}}
                  advancedExpenses={advancedExpenses}
                  readOnly
                  collapsible
                  defaultCollapsed
                />
                <OperatingExpensesSection
                  expenses={property}
                  onChange={() => {}}
                  advancedExpenses={advancedExpenses}
                  readOnly
                  collapsible
                  defaultCollapsed
                />
              </div>
            )}

            <div className="space-y-8">
            <div data-onboarding="scenario-financing">
            <FinancingSection
              financing={scenario.financing}
              onChange={handleFinancingChange}
            />

            </div>
            <div data-onboarding="scenario-acquisition-costs">
            <AcquisitionCosts
              costs={scenario.acquisitionCosts}
              onChange={handleCostsChange}
              advancedExpenses={advancedExpenses}
              analysis={{ acquisitionCosts: computeTotalCosts() }}
              lockedFields={lockedFields}
              financingType={scenario.financing.financingType}
            />
            </div>
            </div>

            <div className="flex justify-end">
              {onSaved && (
                <button
                  type="button"
                  data-onboarding="scenario-save"
                  onClick={handleSave}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('save')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

